import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { saveProductos, getCategorias, createCategoria } from "../../services";
import '../../adminStyles.css';

function AgregarProductos({ onProductUpdate, onClose }) {
    const [nombre, setNombre] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [categoria, setCategoria] = useState("");
    const [nuevaCategoria, setNuevaCategoria] = useState("");
    const [precio, setPrecio] = useState("");
    const [categorias, setCategorias] = useState([]);
    const [crearNuevaCategoria, setCrearNuevaCategoria] = useState(false);
    const [tipoWhisky, setTipoWhisky] = useState("");
    const [destacado, setDestacado] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const inputFileRef = useRef();

    useEffect(() => {
        getCategorias()
            .then((res) => { if (res.status === 200) setCategorias(res.data.categorias); })
            .catch(console.error);
    }, []);

    const handleCategoriaChange = (e) => {
        const v = e.target.value;
        setCrearNuevaCategoria(v === "crear");
        setCategoria(v !== "crear" ? v : "");
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const tipos = ["image/jpeg", "image/jpg", "image/png"];
        if (!tipos.includes(file.type)) {
            setFeedback({ type: "error", msg: "Solo se permiten imágenes JPG, JPEG o PNG." });
            e.target.value = "";
            return;
        }
        setPreviewUrl(URL.createObjectURL(file));
        setFeedback(null);
    };

    const resetForm = () => {
        setNombre(""); setDescripcion(""); setPrecio("");
        setCategoria(""); setNuevaCategoria(""); setTipoWhisky("");
        setDestacado(false); setFeedback(null); setPreviewUrl(null);
        if (inputFileRef.current) inputFileRef.current.value = "";
    };

    const handleSubmit = async () => {
        let categoriaFinal = categoria;

        if (crearNuevaCategoria && nuevaCategoria.trim()) {
            try {
                const res = await createCategoria(nuevaCategoria);
                categoriaFinal = res.data.categoria._id;
            } catch {
                setFeedback({ type: "error", msg: "Error al crear nueva categoría." });
                return;
            }
        }

        if (!nombre || !categoriaFinal || !precio || !tipoWhisky || !inputFileRef.current?.files[0]) {
            setFeedback({ type: "error", msg: "Completá todos los campos obligatorios." });
            return;
        }

        const formData = new FormData();
        formData.append("nombre", nombre);
        formData.append("descripcion", descripcion.trim() || "Sin descripción");
        formData.append("categoria", categoriaFinal);
        formData.append("precio", precio);
        formData.append("tipoWhisky", tipoWhisky);
        formData.append("destacado", destacado ? "true" : "false");
        formData.append("imagen", inputFileRef.current.files[0]);

        setLoading(true);
        try {
            await saveProductos(formData);
            await onProductUpdate();
            setFeedback({ type: "success", msg: "Producto agregado con éxito." });
            setTimeout(() => onClose(), 1200);
        } catch (error) {
            console.error("Error al guardar producto:", error.response?.data || error);
            setFeedback({ type: "error", msg: "Error al guardar el producto. Intentá de nuevo." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-form-modal">
            <div className="admin-form-header">
                <div>
                    <span className="admin-form-eyebrow">Catálogo</span>
                    <h2 className="admin-form-title">Nuevo Whisky</h2>
                </div>
                <button className="admin-modal-close" onClick={onClose} aria-label="Cerrar"><FontAwesomeIcon icon={faXmark} /></button>
            </div>

            {feedback && (
                <div className={`admin-form-feedback admin-form-feedback--${feedback.type}`}>
                    {feedback.msg}
                </div>
            )}

            <div className="admin-form-body">
                <div>
                    <div className="admin-img-upload" onClick={() => inputFileRef.current?.click()}>
                        <input
                            type="file"
                            ref={inputFileRef}
                            accept="image/jpeg,image/jpg,image/png"
                            style={{ display: "none" }}
                            onChange={handleImageChange}
                        />
                        {previewUrl ? (
                            <img src={previewUrl} alt="preview" className="admin-img-preview" />
                        ) : (
                            <div className="admin-img-upload-placeholder">
                                <FontAwesomeIcon icon={faCloudArrowUp} className="admin-img-upload-icon" />
                                <span>Cargar imagen</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="admin-form-fields">
                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label className="admin-form-label">Nombre / Marca *</label>
                            <input
                                className="admin-form-input"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                placeholder="Ej: Johnnie Walker Black"
                            />
                        </div>
                        <div className="admin-form-group">
                            <label className="admin-form-label">Precio *</label>
                            <input
                                className="admin-form-input"
                                type="number"
                                value={precio}
                                onChange={(e) => setPrecio(e.target.value)}
                                placeholder="$"
                            />
                        </div>
                    </div>

                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label className="admin-form-label">Categoría *</label>
                            <select className="admin-form-select" value={categoria} onChange={handleCategoriaChange}>
                                <option value="">Seleccioná una categoría</option>
                                {categorias.map((cat) => (
                                    <option key={cat._id} value={cat._id}>{cat.nombre}</option>
                                ))}
                                <option value="crear">+ Crear nueva categoría</option>
                            </select>
                            {crearNuevaCategoria && (
                                <input
                                    className="admin-form-input"
                                    style={{ marginTop: "0.4rem" }}
                                    placeholder="Nombre de la nueva categoría"
                                    onChange={(e) => setNuevaCategoria(e.target.value)}
                                />
                            )}
                        </div>
                        <div className="admin-form-group">
                            <label className="admin-form-label">Tipo de Whisky *</label>
                            <select
                                className="admin-form-select"
                                value={tipoWhisky}
                                onChange={(e) => setTipoWhisky(e.target.value)}
                            >
                                <option value="">Seleccionar tipo</option>
                                <option value="Blend">Blend</option>
                                <option value="Blend Scotch">Blend Scotch</option>
                                <option value="Bourbon">Bourbon</option>
                                <option value="Irish Malted">Irish</option>
                                <option value="Single Malt">Single Malt</option>
                            </select>
                        </div>
                    </div>

                    <div className="admin-form-group">
                        <label className="admin-form-label">Descripción</label>
                        <textarea
                            className="admin-form-textarea"
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            placeholder="Notas de cata, origen, añada..."
                        />
                    </div>

                    <label className="admin-form-check">
                        <input
                            type="checkbox"
                            checked={destacado}
                            onChange={(e) => setDestacado(e.target.checked)}
                        />
                        <span className="admin-form-check-label">Marcar como destacado</span>
                    </label>
                </div>
            </div>

            <div className="admin-form-footer">
                <button className="admin-btn-danger-ghost" onClick={resetForm}>Limpiar</button>
                <button className="admin-btn-primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? "Guardando..." : "Agregar Whisky"}
                </button>
            </div>
        </div>
    );
}

export default AgregarProductos;
